import type { LegacyRef } from 'react';
import type React from 'react';
import { useState } from 'react';
import type { ApiTag } from '../../../api/services/config/tags/TagService';

// Component props.
export interface TagInputProps<T extends { id: number; name?: string }> {
  initialValues?: T[];
  initialTags?: ApiTag[];
  predicate: (input: string[]) => (data: T) => boolean;
  selectionValues: T[];
  handleSelectedValues?: (values: T[]) => void;
}

function TagInput<T extends { id: number; name: string }>(
  {
    selectionValues = [],
    handleSelectedValues,
    predicate,
    initialTags,
  }: TagInputProps<T>,
  _ref: LegacyRef<HTMLDivElement> | undefined
) {
  const [tagList, setTagList] = useState<ApiTag[]>(
    initialTags ? initialTags : []
  );
  const [filteredSelectionValues, setFilteredSelectionValues] =
    useState<T[]>(selectionValues);
  const [inputValue, setInputValue] = useState<string>('');

  const addTag = (value: string) => {
    if (!value) return;

    // Check if the tag already exists in the tag list
    const tagAlreadyExists = tagList.some((tag) => tag.name === value);

    if (!tagAlreadyExists) {
      const tagToAdd = selectionValues.find((tag) => tag.name === value) || {
        name: value,
      };
      const newTags = [...tagList, tagToAdd as ApiTag];
      setTagList(newTags);
      handleSelectedValues?.(newTags as unknown as T[]);
    }

    // Reset input value and filtered selection values
    setInputValue('');
    setFilteredSelectionValues(selectionValues || []);
  };

  const deleteTag = (value: string) => {
    const newTags = tagList.filter((tag) => tag.name !== value);
    setTagList(newTags);

    const updatedSelections =
      selectionValues?.filter((item) =>
        newTags.some((tag) => tag.name === item.name)
      ) || [];
    handleSelectedValues?.(updatedSelections);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setFilteredSelectionValues(selectionValues.filter(predicate([value])));
  };

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const value = inputValue.trim();
      addTag(value);
    }
  };

  const onSuggestionClick = (name: string) => {
    addTag(name);
  };
  return (
    <div className="uk-margin">
      <div className="tag-input-wrapper">
        {tagList.map((tag) => (
          <div className="tag-element" key={tag.name}>
            {tag.name}
            <button className="delete-tag" onClick={() => deleteTag(tag.name)}>
              <span uk-icon="icon: close; ratio: 0.8"></span>
            </button>
          </div>
        ))}
        <input
          className="uk-input tag-input"
          type="text"
          placeholder="Filtrer par tag"
          value={inputValue}
          onChange={onInputChange}
          onKeyDown={onInputKeyDown}
        />
        {filteredSelectionValues.length > 0 && inputValue && (
          <ul className="suggestions-list">
            {filteredSelectionValues
              .filter((s) => s.name !== undefined)
              .map((item) => (
                <li
                  key={item.id}
                  style={{ padding: '0.5rem', cursor: 'pointer' }}
                  onClick={() => onSuggestionClick(item.name)}
                >
                  {item.name}
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default TagInput;
